import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import dayjs from "dayjs";
import { isEmpty } from "lodash";
import { FC, useRef } from "react";
import { FieldValues, FormProvider, UseFormReturn } from "react-hook-form";
import { useGetLoggedInUserDetails } from "../../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import {
  MuiBox,
  MuiButtonTertiary,
  MuiDateFieldFormController,
  MuiDoubleLabel,
  MuiGrid,
  MuiModal,
} from "../../../../components";
import {
  gridSize,
  MODULE_PERMISSIONS,
  PAGE_IDS,
} from "../../../../helpers/constants";
import { getDateFormatByCountry } from "../../../../helpers/constants/countryDateFormat";
import {
  convertToUserFormat,
  getValueOrBlank,
} from "../../../../helpers/utils";
import { useNavigationSiteCore } from "../../../../hooks/useNavigationSiteCore";
import { usePermissions } from "../../../../hooks/usePermissions";
import { useSiteCore } from "../../../../hooks/useSiteCore";
import { LabelWithIconText } from "../../../../styles/common.style";
import ParticipantActivityTimeline from "../../../createProgram/components/createProgramTabs/tabs/participant/components/participantActivityTimeline/ParticipantActivityTimeline";
import {
  ParticipantDetail,
  ParticipantTabFormKey,
} from "../../../createProgram/modelsAndSchemas/Models";
import { activityTimelineSiteCoreKeys } from "../../../createProgram/siteCore/SiteCoreKeys";
import { pageId } from "../../cpEditParticipantMain/CPEditParticipantMain";
import { CPParticipantActivityTimelineWrapper } from "./CPParticipantActivityTimeline.style";

interface CPParticipantActivityTimelineProps {
  programId: string;
  participantDetail?: ParticipantDetail;
  showModifyNextContactDateModal?: boolean;
  handleUpdateModifyNextContactDateClick?: () => void;
  setShowModifyNextContactDateModal?: (value: boolean) => void;
  displayAddBtn?: boolean;
  isEditProgram?: boolean;
  participantActivityTimelineMethods: UseFormReturn<
    FieldValues,
    any,
    undefined
  >;
}
export const CPParticipantActivityTimeline: FC<
  CPParticipantActivityTimelineProps
> = ({
  programId,
  participantDetail,
  showModifyNextContactDateModal = false,
  handleUpdateModifyNextContactDateClick,
  setShowModifyNextContactDateModal,
  displayAddBtn = false,
  isEditProgram = false,
  participantActivityTimelineMethods,
}) => {
  const { commonLabels, useFetchLabel } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();

  const { hasModulePermission } = usePermissions();
  const { loggedInUser } = useGetLoggedInUserDetails();
  const userCountryDateFormat = getDateFormatByCountry(
    loggedInUser?.countryCode ?? "",
  );

  const { control, clearErrors, setValue } = participantActivityTimelineMethods;

  const {
    activityTimeline,
    nextContactDate,
    modifyNextContactDatePopupHeader,
  } = activityTimelineSiteCoreKeys;

  const getParticipantActivityLabel = useFetchLabel(activityTimeline);

  const openModifyNextContactDateModal = () => {
    setShowModifyNextContactDateModal?.(true);
  };

  const closeModifyNextContactDateModal = () => {
    clearErrors(ParticipantTabFormKey.nextContactDate);
    setShowModifyNextContactDateModal?.(false);
  };

  const getDateValue = () => {
    const nextContactDateFormValue = convertToUserFormat(
      participantDetail?.nextContactDate,
      userCountryDateFormat,
    );
    const value = nextContactDateFormValue ?? null;
    const dateValue = dayjs(value, userCountryDateFormat);
    setValue(ParticipantTabFormKey.nextContactDate, dateValue);
    return dateValue;
  };

  const renderModifyNextContactDate = () => {
    return (
      <MuiBox className="modify-next-contact-date-container">
        <MuiBox>
          <MuiDoubleLabel
            label={getParticipantActivityLabel(nextContactDate)}
            replaceEmptyWithDash={isEmpty(participantDetail?.nextContactDate)}
            value={getValueOrBlank(
              convertToUserFormat(
                participantDetail?.nextContactDate,
                userCountryDateFormat,
              ),
            )}
          />
        </MuiBox>
        <MuiButtonTertiary
          onClick={openModifyNextContactDateModal}
          aria-label={commonLabels.modify}
        >
          <LabelWithIconText>
            <EditOutlinedIcon /> {commonLabels.modify}
          </LabelWithIconText>
        </MuiButtonTertiary>
      </MuiBox>
    );
  };

  return (
    <CPParticipantActivityTimelineWrapper>
      <FormProvider {...participantActivityTimelineMethods}>
        <ParticipantActivityTimeline
          pageId={PAGE_IDS.COACH_PORTAL_VIEW_PARTICIPANT_ACTIVITY_TIMELINE}
          isEditProgram={
            hasModulePermission(
              MODULE_PERMISSIONS.COACH_PORTAL_EDIT_PARTICIPANT_ACTIVITY_TIMELINE,
            )
              ? isEditProgram
              : false
          }
          programId={programId}
          displayAddBtn={displayAddBtn}
          isFromCoachPortal
          showMoreOptions={false}
          nextContactDate={participantDetail?.nextContactDate}
          renderCustomNextContactDate={renderModifyNextContactDate}
          hasDeletePermission={hasModulePermission(
            MODULE_PERMISSIONS.COACH_PORTAL_DELETE_PARTICIPANT_ACTIVITY_TIMELINE,
          )}
        />
      </FormProvider>

      <MuiModal
        keepMounted
        open={showModifyNextContactDateModal}
        title={getParticipantActivityLabel(modifyNextContactDatePopupHeader)}
        primaryButtonTitle={commonLabels.update}
        secondaryButtonTitle={commonLabels.cancel}
        onClose={closeModifyNextContactDateModal}
        onPrimaryButtonClick={handleUpdateModifyNextContactDateClick}
        onSecondaryButtonClick={closeModifyNextContactDateModal}
      >
        <CPParticipantActivityTimelineWrapper>
          <MuiGrid container>
            <MuiGrid size={gridSize}>
              <MuiDateFieldFormController
                control={control}
                name={ParticipantTabFormKey.nextContactDate}
                id={`${pageId}-${ParticipantTabFormKey.nextContactDate}`}
                value={getDateValue()}
                label={getParticipantActivityLabel(nextContactDate)}
                valueFormat={userCountryDateFormat}
                format={userCountryDateFormat}
                disablePast
                clearable={true}
                requiredARIALabel={commonARIALabel.required}
                selectDateARIALabel={commonARIALabel.selectDate}
              />
            </MuiGrid>
          </MuiGrid>
        </CPParticipantActivityTimelineWrapper>
      </MuiModal>
    </CPParticipantActivityTimelineWrapper>
  );
};
